import { cloneAuthoring, stringifyAuthoringJSON } from "./authoring-json.mjs";

export function subjectDefinition(node, context) {
  if (node?.type !== "platform_block" || context?.kind !== "triproutes") return null;
  const definition=context.nodes?.[node.id] || context.blockDefinitions?.[node.blockRef?.id];
  if (!definition || definition.blockRef && !pinEqual(definition.blockRef,node.blockRef)) return null;
  return definition;
}
export function editableSubject(document, context) {
  return document?.authoringVersion === "1.0" && context?.kind === "triproutes" && context.editable === true && Boolean(document.validationProfileRef) && Array.isArray(document.nodes) && document.nodes.every(node=>["input","result"].includes(node.type) || node.type === "platform_block" && subjectDefinition(node,context) || node.type === "ai" && context.genericAllowed === true);
}

// Use the registered stage default, bounded by the selected model. An explicit
// saved limit is a user setting and must never change during refresh.
export function withSubjectTokenDefault(node, context) {
  if (context?.editable !== true || node.parameters?.values?.maxOutputTokens != null || !Object.keys(node.modelSlots || {}).length) return node;
  const definition=subjectDefinition(node, context);
  const field=definition?.parameterFields?.find(field=>field.key === "maxOutputTokens" && field.type === "integer");
  if (!field) return node;
  const positive=value=>Number.isSafeInteger(Number(value)) && Number(value)>0 ? Number(value) : null;
  if (field.max != null && !positive(field.max)) return node;
  const ceilings=[positive(definition.nodeTemplate?.parameters?.values?.maxOutputTokens) || 24000];
  if (positive(field.max)) ceilings.push(Number(field.max));
  for (const slot of definition.modelSlots || []) {
    const ref=node.modelSlots[slot.id], choice=slot.choices?.find(choice=>choice.choiceRef === ref);
    if (choice && !positive(choice.maxOutputTokens)) return node;
    const maximum=choice?.maxOutputTokens ?? (ref === slot.currentChoiceRef ? node.parameters?.sourceModelBindings?.[slot.id]?.maxOutputTokens : null);
    if (positive(maximum)) ceilings.push(Number(maximum));
  }
  const value=Math.min(...ceilings);
  if (value < (positive(field.min) || 1)) return node;
  const next=cloneAuthoring(node);
  next.parameters={...next.parameters,values:{...next.parameters?.values,maxOutputTokens:value}};
  return next;
}

export function withSubjectDefaults(document, context) {
  if (!editableSubject(document, context)) return document;
  const nodes=document.nodes.map(node=>withSubjectTokenDefault(node, context));
  return nodes.some((node,index)=>node !== document.nodes[index]) ? {...document,nodes} : document;
}

// Apply only a currently admitted choice with the exact captured provider/model.
// No aliases, replacements, user-chosen models or oversized limits are rewritten.
// The resulting document still needs an explicit save and server validation.
export function withVerifiedSourceModels(document, context) {
  if (!editableSubject(document,context)) return {document,changed:0};
  let changed=0;
  const nodes=document.nodes.map(node=>{
    const definition=subjectDefinition(node,context);if (!definition) return node;
    let next=node;
    for(const slot of definition.modelSlots || []){
      const current=next.modelSlots?.[slot.id],binding=next.parameters?.sourceModelBindings?.[slot.id];
      if(!current?.startsWith("legacy-unadmitted-")||current!==slot.currentChoiceRef||!binding?.provider||!binding?.model)continue;
      const limit=next.parameters?.values?.maxOutputTokens;
      const choices=(slot.choices || []).filter(choice=>choice.provider===binding.provider&&choice.model===binding.model&&Number.isSafeInteger(Number(choice.maxOutputTokens))&&Number(choice.maxOutputTokens)>0&&(limit==null||Number(limit)<=Number(choice.maxOutputTokens))).sort((a,b)=>Number(b.maxOutputTokens)-Number(a.maxOutputTokens)||a.choiceRef.localeCompare(b.choiceRef));
      if(!choices.length)continue;
      next=setSubjectValue(next,context,"modelSlots",slot.id,choices[0].choiceRef);changed++;
    }
    return next;
  });
  return {document:changed ? {...document,nodes} : document,changed};
}
export function subjectModelCheckHref(node,context,site="ru") {
  const definition=subjectDefinition(node,context),slot=definition?.modelSlots?.[0],selected=slot?.choices?.find(choice=>choice.choiceRef===node?.modelSlots?.[slot.id]),binding=selected?.provider&&selected?.model ? selected : Object.values(node?.parameters?.sourceModelBindings || {})[0];
  const query=new URLSearchParams({site});
  if(context?.solutionId&&definition?.checkStageId&&binding?.provider&&binding?.model){query.set("checkProvider",binding.provider);query.set("checkModel",binding.model);query.set("checkSolution",context.solutionId);query.set("checkStage",definition.checkStageId);}
  return `#/settings/models?${query}`;
}
export function dependencies(node) {
  if (node?.type !== "platform_block") return node?.dependsOn || [];
  return [...new Set(Object.values(node.inputBindings || {}).flatMap(binding=>binding.nodeIds || (binding.nodeId ? [binding.nodeId] : [])))];
}
export function graphLinks(document) {
  return document.nodes.flatMap(node=>node.type === "platform_block" ? Object.entries(node.inputBindings || {}).flatMap(([port,binding])=>(binding.nodeIds || [binding.nodeId]).filter(Boolean).map(source=>({id:`${source}:${node.id}:${port}`,source,target:node.id,inputPort:port,targetHandle:port}))) : (node.dependsOn || []).map(source=>({id:`${source}:${node.id}`,source,target:node.id})));
}
export function setSubjectValue(node, context, section, key, value) {
  const definition=subjectDefinition(node,context);
  if (!definition || context.editable !== true) throw new Error("Редактирование этого этапа не разрешено");
  const next=cloneAuthoring(node);
  if (section === "modelSlots") {
    const slot=definition.modelSlots?.find(slot=>slot.id === key);
    const choice=slot?.choices?.find(choice=>choice.choiceRef === value);
    if (slot?.optional && value === "") { next.modelSlots={...next.modelSlots};delete next.modelSlots[key];return next; }
    if (!choice) throw new Error("Модель не допущена для этого этапа");
    next.modelSlots={...next.modelSlots,[key]:value};
    // Keep an explicit limit within the verified envelope of the new model.
    // An unfinished draft may omit it; test/publication still require it.
    if (choice.maxOutputTokens > 0 && Number(next.parameters?.values?.maxOutputTokens) > choice.maxOutputTokens) {
      next.parameters={...next.parameters,values:{...next.parameters.values,maxOutputTokens:choice.maxOutputTokens}};
    }
    return withSubjectTokenDefault(next, context);
  }
  const fields=section === "instructions" ? definition.instructionFields : section === "values" ? definition.parameterFields : null;
  if (!fields?.some(field=>field.key === key)) throw new Error("Поле этапа нельзя изменить");
  const values={...next.parameters?.[section]};
  if (value === undefined) delete values[key]; else values[key]=value;
  next.parameters={...next.parameters,[section]:values};
  return next;
}
export function removeSubjectLink(source, edge) {
  const document=cloneAuthoring(source),target=document.nodes.find(node=>node.id === edge.target);
  if (!target) throw new Error("Этап связи не найден");
  if (target.type !== "platform_block") target.dependsOn=(target.dependsOn || []).filter(id=>id !== edge.source);
  else {
    const ports=edge.inputPort ? [edge.inputPort] : Object.keys(target.inputBindings || {});
    for (const port of ports) {
      const binding=target.inputBindings?.[port]; if (!binding) continue;
      const ids=(binding.nodeIds || [binding.nodeId]).filter(id=>id && id !== edge.source);
      if (!ids.length) delete target.inputBindings[port];
      else target.inputBindings[port]=binding.nodeIds ? {nodeIds:ids} : {nodeId:ids[0]};
    }
  }
  return document;
}
const pinEqual=(a,b)=>Boolean(a && b && a.id===b.id && a.version===b.version && a.digest===b.digest);
export function compatibleConnection(document,context,sourceID,targetID,portID) {
  if (!editableSubject(document,context) || !context.graphEdit || sourceID===targetID) return null;
  const source=document.nodes.find(node=>node.id===sourceID),target=document.nodes.find(node=>node.id===targetID);
  if (!source || !target || source.type === "result" || target.type === "input") return null;
  const explicit=context.allowedConnections?.find(link=>link.source === sourceID && link.target === targetID && (!portID || link.targetPort === portID));
  if (explicit) return explicit;
  const sourceSchema=source.type === "input" ? context.inputSchemaRef || document.inputSchemaRef : subjectDefinition(source,context)?.outputSchemaRef;
  const ports=target.type === "result" ? [{id:"",schemaRef:context.resultSchemaRef || document.schemas?.[document.resultSchemaKey]?.schemaRef}] : subjectDefinition(target,context)?.inputPorts || [];
  const port=ports.find(port=>(!portID || port.id===portID) && pinEqual(sourceSchema,port.schemaRef));
  return port ? {source:sourceID,target:targetID,targetPort:port.id} : null;
}
export function connectSubjectNodes(source,context,from,to,port,replaced=null) {
  const connection=compatibleConnection(source,context,from,to,port);
  if (!connection) throw new Error("Контракты этих портов не совместимы");
  const document=replaced ? removeSubjectLink(source,replaced) : cloneAuthoring(source),target=document.nodes.find(node=>node.id===to);
  const reaches=(id,seen=new Set())=>{if(id===to)return true;if(seen.has(id))return false;seen.add(id);return dependencies(document.nodes.find(node=>node.id===id)).some(parent=>reaches(parent,seen));};
  if(reaches(from))throw new Error("Связь создаёт цикл");
  if(graphLinks(document).length>=200)throw new Error("Не больше 200 связей");
  if(target.type !== "platform_block") {
    if(target.dependsOn?.includes(from))throw new Error("Такая связь уже есть");
    target.dependsOn=[...(target.dependsOn || []),from];
  } else {
    const key=connection.targetPort,existing=target.inputBindings?.[key],ids=existing?.nodeIds || (existing?.nodeId ? [existing.nodeId] : []);
    if(ids.includes(from))throw new Error("Такая связь уже есть");
    const multi=subjectDefinition(target,context)?.inputPorts?.find(port=>port.id===key)?.multiple;
    if(ids.length && !multi)throw new Error("Порт уже подключён. Переподключите его связь");
    target.inputBindings={...target.inputBindings,[key]:multi ? {nodeIds:[...ids,from]} : {nodeId:from}};
  }
  return document;
}
export function addSubjectNode(source,context,blockID,copyID="") {
  if(!editableSubject(source,context) || !context.graphEdit || source.nodes.length>=50)throw new Error("Добавление этапа недоступно");
  const original=copyID ? source.nodes.find(node=>node.id===copyID) : null;
  const definition=original ? subjectDefinition(original,context) : context.blockDefinitions?.[blockID];
  if(!definition || definition.addable === false || original && definition.copyable === false || !original && !definition.nodeTemplate)throw new Error("Этот этап нельзя добавить");
  const document=cloneAuthoring(source),template=cloneAuthoring(original || definition.nodeTemplate);
  let index=1;while(document.nodes.some(node=>node.id===`stage_${index}`))index++;
  template.id=`stage_${index}`;template.name=original ? `${(original.name || definition.label || "Этап").slice(0,105)} (копия)` : definition.label || "Новый этап";template.inputBindings={};
  document.nodes.splice(document.nodes.length-1,0,withSubjectTokenDefault(template,context));
  return {document,id:template.id};
}

export function removeSubjectNode(source, context, id) {
  if (!editableSubject(source,context) || !context.graphEdit) throw new Error("Изменение графа недоступно");
  const node=source.nodes.find(node=>node.id===id);
  if (!node || ["input","result"].includes(node.type)) throw new Error("Вход и результат нельзя удалить");
  let document=cloneAuthoring(source);
  for (const link of graphLinks(document).filter(link=>link.source===id)) document=removeSubjectLink(document,link);
  document.nodes=document.nodes.filter(node=>node.id!==id);
  return document;
}

export function subjectTestDocument(document, context) {
  return editableSubject(document,context) && context.inputSchema ? {...document,inputSchema:context.inputSchema,inputForm:context.inputForm || {},inputAdapter:context.inputAdapter} : document;
}
